import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Site footer: brand + contact, then four short link groups (two per row on phones).
 * Spacing uses bracket values where Bootstrap's !important p/m/gap-3..5 would override Tailwind's.
 */

const MESSENGER_URL = "https://m.me/guideline2germany";

const Footer = () => {
  const { t, i18n } = useTranslation();

  const linkGroups = [
    {
      title: t("footer.explore"),
      links: [
        { to: "/course/courses", label: t("footer.courses") },
        { to: "/ausbildung", label: t("nav.findAusbildung") },
        { to: "/fsj-bfd-circular", label: t("nav.fsjBfdCircular") },
        { to: "/pages/book-appointment", label: t("footer.bookAppointment") },
        { to: "/document-translation", label: t("translationPromo.label") },
      ],
    },
    {
      title: t("footer.guides"),
      links: [
        { to: "/ausbildung-in-germany-from-bangladesh", label: t("footer.bangladeshGuide") },
        { to: "/fsj-bfd-in-germany-from-bangladesh", label: t("footer.fsjBfdGuide") },
        { to: "/learn-german-language-in-bangladesh", label: t("footer.germanGuide") },
        { to: "/study-in-germany-from-bangladesh", label: t("footer.studyGuide") },
      ],
    },
    {
      title: t("footer.tools"),
      links: [
        { to: "/pages/check-eligibility", label: t("footer.eligibilityCheck") },
        { to: "/cv-motivation-letter-check", label: t("usefulTools.tools.cvCheck.title") },
        { to: "/visa-waiting-time-tracker", label: t("usefulTools.tools.visaTracker.title") },
        { to: "/german-student-visa-checklist", label: t("usefulTools.tools.visaChecklist.title") },
        { to: "/blocked-account-calculator-student", label: t("usefulTools.tools.blockedCalculator.title") },
        { to: "/study-in-germany-after-hsc#helper", label: t("usefulTools.tools.hscPath.title") },
        { to: "/ausbildung-salary-calculator", label: t("usefulTools.tools.costCalculator.title") },
        { to: "/free-tools-and-guides", label: t("toolsHub.footerLink") },
      ],
    },
    {
      title: t("footer.company"),
      links: [
        { to: "/pages/about-us", label: t("footer.aboutUs") },
        { to: "/pages/contact-us", label: t("footer.contactUs") },
        { to: "/pages/faq", label: t("footer.faq") },
        { to: "/pages/blog", label: t("footer.blog") },
      ],
    },
  ];

  return (
    <footer className="bg-[#009fe3] text-white pt-10 pb-5 mt-16">
      <div className="max-w-7xl mx-auto px-[1rem]">
        <div className="grid grid-cols-2 lg:grid-cols-[1.35fr_1fr_1fr_1.15fr_0.8fr] gap-x-[1.5rem] gap-y-8">
          {/* Brand + contact */}
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-[0.75rem] mb-[0.75rem]">
              <img src="/assets/img/g2g-logo.png" alt="Guideline2Germany logo" className="w-12 h-8" />
              <span className="text-xl font-bold text-white">Guideline2Germany</span>
            </div>
            <p className="text-sm leading-relaxed !text-white/85 mb-[1rem] max-w-sm">{t("footer.tagline")}</p>
            <ul className="list-none pl-0 mb-0 space-y-1.5 text-sm text-white/90">
              <li>
                <a href="mailto:info@guideline2germany.com" className="inline-flex items-center gap-2 text-white/90 hover:text-white no-underline">
                  <i className="fa-solid fa-envelope w-4 text-center text-yellow-300" />
                  info@guideline2germany.com
                </a>
              </li>
              <li>
                <a href="tel:+8801867555287" className="inline-flex items-center gap-2 text-white/90 hover:text-white no-underline">
                  <i className="fa-solid fa-phone w-4 text-center text-yellow-300" />
                  +880 186 755 5287
                </a>
              </li>
              <li>
                <a href={MESSENGER_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-white/90 hover:text-white no-underline">
                  <i className="fa-brands fa-facebook-messenger w-4 text-center text-yellow-300" />
                  {t("footer.fbMessenger")}
                </a>
              </li>
            </ul>
          </div>

          {/* Link groups */}
          {linkGroups.map((group) => (
            <nav key={group.title} aria-label={group.title}>
              <h4 className="text-xs font-bold uppercase tracking-widest text-yellow-300 mb-[0.75rem] !text-left">{group.title}</h4>
              <ul className="list-none pl-0 mb-0 space-y-1.5 text-sm">
                {group.links.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="text-white/90 hover:text-white hover:underline no-underline leading-snug">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-8 pt-[1rem] border-t border-white/20 text-center text-xs text-white/75">{t("footer.copyright", { year: new Date().getFullYear().toLocaleString(i18n.language === "bn" ? "bn-BD" : "en-US", { useGrouping: false }) })}</div>
      </div>
    </footer>
  );
};

export default Footer;
